import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
const VIEWPORT = 256;
const OUTPUT = 256;
// Cap on canvas → MediaRecorder recording length when cropping a GIF. The
// browser can't tell us how long one GIF loop is without ImageDecoder, and
// ImageDecoder isn't on every target. Three seconds covers all but the
// longest stickers without bloating the upload.
const GIF_RECORD_SECONDS = 3;
const STILL_RASTER_MIMES = new Set([
    "image/png",
    "image/jpeg",
    "image/webp",
]);
const ANIMATED_RASTER_MIMES = new Set([
    "image/gif",
]);
const VIDEO_MIMES = new Set([
    "video/mp4",
    "video/webm",
]);
const SVG_MIME = "image/svg+xml";
export const ICON_ALLOWED_MIMES = [
    ...STILL_RASTER_MIMES,
    ...ANIMATED_RASTER_MIMES,
    SVG_MIME,
    ...VIDEO_MIMES,
];
export const ICON_ALLOWED_ACCEPT = ICON_ALLOWED_MIMES.join(",");
function detectKind(file) {
    if (STILL_RASTER_MIMES.has(file.type))
        return "raster";
    if (ANIMATED_RASTER_MIMES.has(file.type))
        return "gif";
    if (VIDEO_MIMES.has(file.type))
        return "video";
    return "svg";
}
// Square cropper for workspace icons (KTK-226). All upload types ride the
// same drag/pan + zoom UX; the export pipeline branches by kind:
//
//   raster (PNG/JPEG/WebP) → canvas drawImage → PNG/WebP base64
//   gif                    → canvas + MediaRecorder rAF capture → WebM
//   svg                    → mutate viewBox + width/height       → SVG XML
//   video (MP4/WebM)       → canvas + MediaRecorder rAF capture → WebM
//
// Animated outputs always become WebM because that's the only animation
// container browsers can write without an external codec lib, and rocky's
// Hub already accepts video/webm.
export function IconCropper({ file, onCancel, onConfirm }) {
    const kind = detectKind(file);
    const [src, setSrc] = useState(null);
    const [svgText, setSvgText] = useState(null);
    const [srcDims, setSrcDims] = useState(null);
    const [scale, setScale] = useState(1);
    const [minScale, setMinScale] = useState(1);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);
    const [busy, setBusy] = useState(false);
    const [recordingProgress, setRecordingProgress] = useState(null);
    const [err, setErr] = useState(null);
    const draggingRef = useRef(null);
    const imgRef = useRef(null);
    const videoRef = useRef(null);
    // Load the source as an object URL (for raster / gif / svg / video preview)
    // and, for SVG, as text so we can mutate the viewBox on confirm.
    useEffect(() => {
        let cancelled = false;
        const url = URL.createObjectURL(file);
        setSrc(url);
        if (kind === "svg") {
            file.text().then((t) => {
                if (!cancelled)
                    setSvgText(t);
            });
        }
        return () => {
            cancelled = true;
            URL.revokeObjectURL(url);
        };
    }, [file, kind]);
    // Once the source's intrinsic dimensions are known, fit the smaller
    // dimension to the viewport (smaller-side-zoom) so the entire image
    // is visible at minimum zoom.
    const handleLoaded = (w, h) => {
        if (w <= 0 || h <= 0)
            return;
        const fitScale = Math.max(VIEWPORT / w, VIEWPORT / h);
        setSrcDims({ w, h });
        setMinScale(fitScale);
        setScale(fitScale);
        setTx((VIEWPORT - w * fitScale) / 2);
        setTy((VIEWPORT - h * fitScale) / 2);
    };
    const onMouseDown = (e) => {
        draggingRef.current = { x: e.clientX - tx, y: e.clientY - ty };
    };
    const onMouseMove = (e) => {
        const d = draggingRef.current;
        if (!d || !srcDims)
            return;
        const w = srcDims.w * scale;
        const h = srcDims.h * scale;
        setTx(clamp(e.clientX - d.x, VIEWPORT - w, 0));
        setTy(clamp(e.clientY - d.y, VIEWPORT - h, 0));
    };
    const onMouseUp = () => {
        draggingRef.current = null;
    };
    const onScaleChange = (e) => {
        if (!srcDims)
            return;
        const nextScale = parseFloat(e.target.value);
        // Pivot zoom around the viewport center so the framed subject doesn't
        // jump out from under the user.
        const cx = (VIEWPORT / 2 - tx) / scale;
        const cy = (VIEWPORT / 2 - ty) / scale;
        const w = srcDims.w * nextScale;
        const h = srcDims.h * nextScale;
        setTx(clamp(VIEWPORT / 2 - cx * nextScale, VIEWPORT - w, 0));
        setTy(clamp(VIEWPORT / 2 - cy * nextScale, VIEWPORT - h, 0));
        setScale(nextScale);
    };
    const handleConfirm = async () => {
        setBusy(true);
        setErr(null);
        try {
            const result = await renderCrop({
                kind,
                file,
                srcDims,
                imgEl: imgRef.current,
                videoEl: videoRef.current,
                svgText,
                scale,
                tx,
                ty,
                onProgress: setRecordingProgress,
            });
            await onConfirm(result);
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
        }
        finally {
            setBusy(false);
            setRecordingProgress(null);
        }
    };
    const ready = !!srcDims;
    return (_jsx("div", { role: "dialog", "aria-modal": "true", 
        // Stop click + mouseup propagation. The cropper is rendered inside
        // WorkspaceSettingsDialog's outer backdrop, which has its own
        // close-on-click handler — without this, drag mouseups and backdrop
        // clicks here bubble up and dismiss the parent dialog (which would
        // unmount the cropper).
        onClick: (e) => e.stopPropagation(), onMouseUp: (e) => e.stopPropagation(), style: {
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
        }, children: _jsxs("div", { style: {
                background: "var(--bg-secondary)",
                border: "1px solid var(--border)",
                borderRadius: 10,
                padding: 20,
                minWidth: 320,
                color: "var(--text-primary)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }, children: [_jsx("h3", { style: { margin: 0, fontSize: 14, fontWeight: 600 }, children: "Crop workspace icon" }), _jsxs("p", { style: { margin: "4px 0 12px", fontSize: 11, color: "var(--text-muted)" }, children: ["Drag to pan, slider to zoom. Crops to a square.", kind === "gif" && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx("span", { children: "Cropped GIFs are saved as WebM video to keep their animation." })] })), kind === "video" && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx("span", { children: "Cropped video is re-encoded as WebM. Saving may take a few seconds." })] }))] }), _jsxs("div", { style: {
                        width: VIEWPORT,
                        height: VIEWPORT,
                        background: "var(--bg-primary)",
                        borderRadius: 8,
                        overflow: "hidden",
                        position: "relative",
                        cursor: draggingRef.current ? "grabbing" : "grab",
                        userSelect: "none",
                    }, onMouseDown: onMouseDown, onMouseMove: onMouseMove, onMouseUp: onMouseUp, onMouseLeave: onMouseUp, children: [src && (kind === "raster" || kind === "gif" || kind === "svg") && (
                        // Single img element doubles as both the loader (onLoad sets
                        // srcDims) and the visible preview. Width/height are computed
                        // directly in display pixels — `srcDims.w * scale` × `srcDims.h
                        // * scale` — so the rendered box is always proportional to the
                        // source's natural aspect ratio. No CSS transform that could
                        // accidentally scale one axis differently than the other.
                        _jsx("img", { ref: imgRef, src: src, alt: "", draggable: false, onLoad: (e) => {
                                if (srcDims)
                                    return;
                                const t = e.currentTarget;
                                // SVGs without intrinsic width/height return 0; default to a
                                // square the size of the viewport so user can still crop.
                                const w = t.naturalWidth || (kind === "svg" ? VIEWPORT : 0);
                                const h = t.naturalHeight || (kind === "svg" ? VIEWPORT : 0);
                                handleLoaded(w, h);
                            }, onError: () => setErr("Couldn't decode that file."), style: {
                                position: "absolute",
                                left: srcDims ? tx : 0,
                                top: srcDims ? ty : 0,
                                width: srcDims ? srcDims.w * scale : "auto",
                                height: srcDims ? srcDims.h * scale : "auto",
                                opacity: srcDims ? 1 : 0,
                                pointerEvents: "none",
                                display: "block",
                                maxWidth: "none",
                                maxHeight: "none",
                            } })), src && kind === "video" && (_jsx("video", { ref: videoRef, src: src, autoPlay: true, loop: true, muted: true, playsInline: true, draggable: false, onLoadedMetadata: (e) => {
                                if (srcDims)
                                    return;
                                const v = e.currentTarget;
                                handleLoaded(v.videoWidth, v.videoHeight);
                            }, style: {
                                position: "absolute",
                                left: srcDims ? tx : 0,
                                top: srcDims ? ty : 0,
                                width: srcDims ? srcDims.w * scale : "auto",
                                height: srcDims ? srcDims.h * scale : "auto",
                                opacity: srcDims ? 1 : 0,
                                pointerEvents: "none",
                                display: "block",
                                maxWidth: "none",
                                maxHeight: "none",
                            } }))] }), _jsx("input", { type: "range", min: minScale, max: minScale * 4, step: 0.01, value: scale, onChange: onScaleChange, disabled: !ready, style: { width: "100%", marginTop: 12 }, "aria-label": "Zoom" }), recordingProgress !== null && (_jsxs("div", { style: { marginTop: 8, fontSize: 11, color: "var(--text-muted)" }, children: ["Encoding\u2026 ", Math.round(recordingProgress * 100), "%"] })), err && (_jsx("div", { style: { marginTop: 10, fontSize: 11, color: "var(--error)" }, children: err })), _jsxs("div", { style: { display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }, children: [_jsx("button", { type: "button", onClick: onCancel, disabled: busy, style: btnStyle("ghost"), children: "Cancel" }), _jsx("button", { type: "button", onClick: handleConfirm, disabled: busy || !ready, style: btnStyle("primary"), children: busy ? "Saving…" : "Save" })] })] }) }));
}
async function renderCrop(a) {
    if (!a.srcDims)
        throw new Error("Source not loaded yet.");
    if (a.kind === "raster") {
        if (!a.imgEl)
            throw new Error("Image element not mounted.");
        const canvas = document.createElement("canvas");
        canvas.width = OUTPUT;
        canvas.height = OUTPUT;
        const ctx = canvas.getContext("2d");
        if (!ctx)
            throw new Error("Canvas 2D context unavailable.");
        drawSourceFrame(ctx, a.imgEl, a);
        const mimeType = a.file.type === "image/webp" ? "image/webp" : "image/png";
        return canvasToBase64(canvas, mimeType, 0.92);
    }
    if (a.kind === "svg") {
        if (!a.svgText)
            throw new Error("SVG markup unavailable.");
        const cropped = mutateSvgViewBox(a.svgText, a);
        return { base64: btoaUtf8(cropped), mimeType: "image/svg+xml" };
    }
    if (a.kind === "gif") {
        // Don't reuse the visible cropper img — Chromium's GIF animator can
        // stall when an element is in the middle of layout/CSS work and
        // drawImage gets called on the same tick. Fresh, DOM-attached, rendered
        // (just off-screen) img with `decode()` awaited gives us a known-good
        // animator the rAF loop can sample reliably.
        const url = URL.createObjectURL(a.file);
        const img = document.createElement("img");
        img.src = url;
        img.style.cssText =
            "position:fixed;left:-99999px;top:0;width:auto;height:auto;pointer-events:none;";
        document.body.appendChild(img);
        try {
            await img.decode();
            return await recordToWebm({ source: img, durationMs: GIF_RECORD_SECONDS * 1000 }, a);
        }
        finally {
            img.remove();
            URL.revokeObjectURL(url);
        }
    }
    // video
    if (!a.videoEl)
        throw new Error("Video element not mounted.");
    const v = a.videoEl;
    // Restart playback from 0 so the recording starts at the head of the clip,
    // and stop autoplay-loop so the recorder can finalize on `ended`.
    v.loop = false;
    v.currentTime = 0;
    await v.play().catch(() => { });
    return recordToWebm({
        source: v,
        durationMs: Math.max(500, Math.min(20_000, (v.duration || 5) * 1000)),
    }, a);
}
function drawSourceFrame(ctx, source, a) {
    if (!a.srcDims)
        return;
    const renderScale = OUTPUT / VIEWPORT;
    ctx.clearRect(0, 0, OUTPUT, OUTPUT);
    ctx.drawImage(source, a.tx * renderScale, a.ty * renderScale, a.srcDims.w * a.scale * renderScale, a.srcDims.h * a.scale * renderScale);
}
async function recordToWebm({ source, durationMs }, a) {
    if (typeof MediaRecorder === "undefined") {
        throw new Error("Recording isn't supported in this browser.");
    }
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext("2d");
    if (!ctx)
        throw new Error("Canvas 2D context unavailable.");
    // 30fps captureStream gives us smooth playback in-tile while keeping
    // file size manageable; the recorder pipes from this stream.
    const stream = canvas.captureStream(30);
    const mimeCandidates = [
        "video/webm;codecs=vp9",
        "video/webm;codecs=vp8",
        "video/webm",
    ];
    const recorderMime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? "video/webm";
    const recorder = new MediaRecorder(stream, { mimeType: recorderMime });
    const chunks = [];
    recorder.ondataavailable = (e) => {
        if (e.data.size > 0)
            chunks.push(e.data);
    };
    const finished = new Promise((resolve, reject) => {
        recorder.onstop = () => {
            resolve(new Blob(chunks, { type: recorderMime.split(";")[0] }));
        };
        recorder.onerror = (e) => reject(e);
    });
    let raf = 0;
    let stopped = false;
    const start = performance.now();
    const tick = () => {
        if (stopped)
            return;
        const now = performance.now();
        const elapsed = now - start;
        a.onProgress(Math.min(1, elapsed / durationMs));
        drawSourceFrame(ctx, source, a);
        if (elapsed >= durationMs) {
            stopped = true;
            recorder.stop();
            a.onProgress(1);
            return;
        }
        raf = requestAnimationFrame(tick);
    };
    recorder.start();
    raf = requestAnimationFrame(tick);
    // If the source is a video and finishes before durationMs, stop early.
    if (source instanceof HTMLVideoElement) {
        const onEnded = () => {
            if (stopped)
                return;
            stopped = true;
            recorder.stop();
            a.onProgress(1);
        };
        source.addEventListener("ended", onEnded, { once: true });
    }
    let blob;
    try {
        blob = await finished;
    }
    finally {
        cancelAnimationFrame(raf);
        stream.getTracks().forEach((t) => t.stop());
    }
    const arrayBuffer = await blob.arrayBuffer();
    const base64 = arrayBufferToBase64(arrayBuffer);
    return { base64, mimeType: "video/webm" };
}
// Mutate an SVG document's viewBox + width/height so the cropped square is
// what gets rendered. Source-space coordinates are computed from the same
// translate / scale as the visual preview.
function mutateSvgViewBox(svgText, a) {
    if (!a.srcDims)
        return svgText;
    // Source coords corresponding to the viewport's top-left + size.
    const naturalW = a.srcDims.w;
    const naturalH = a.srcDims.h;
    const cropX = -a.tx / a.scale;
    const cropY = -a.ty / a.scale;
    const cropSize = VIEWPORT / a.scale;
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");
    const root = doc.documentElement;
    if (!root || root.nodeName.toLowerCase() !== "svg") {
        throw new Error("Couldn't parse SVG.");
    }
    // The cropped region is in the natural-pixel space of the rendered img.
    // Map back into the SVG's user-space via the existing viewBox.
    const existingVB = root.getAttribute("viewBox");
    let vbX = 0;
    let vbY = 0;
    let vbW = naturalW;
    let vbH = naturalH;
    if (existingVB) {
        const parts = existingVB.split(/[\s,]+/).map(Number);
        if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
            [vbX, vbY, vbW, vbH] = parts;
        }
    }
    const userX = vbX + (cropX / naturalW) * vbW;
    const userY = vbY + (cropY / naturalH) * vbH;
    const userW = (cropSize / naturalW) * vbW;
    const userH = (cropSize / naturalH) * vbH;
    root.setAttribute("viewBox", `${userX} ${userY} ${userW} ${userH}`);
    root.setAttribute("width", String(OUTPUT));
    root.setAttribute("height", String(OUTPUT));
    root.setAttribute("preserveAspectRatio", "xMidYMid meet");
    return new XMLSerializer().serializeToString(doc);
}
function canvasToBase64(canvas, mimeType, quality) {
    const dataUrl = canvas.toDataURL(mimeType, quality);
    return { base64: dataUrl.replace(/^data:[^;]+;base64,/, ""), mimeType };
}
function arrayBufferToBase64(buf) {
    const bytes = new Uint8Array(buf);
    let bin = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    return btoa(bin);
}
function btoaUtf8(s) {
    // The SVG can contain non-Latin-1 characters; round-trip through UTF-8
    // bytes before base64 so we don't blow up on emoji-laden markup.
    const bytes = new TextEncoder().encode(s);
    let bin = "";
    for (let i = 0; i < bytes.length; i++)
        bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
}
function clamp(v, lo, hi) {
    if (lo > hi)
        return lo;
    if (v < lo)
        return lo;
    if (v > hi)
        return hi;
    return v;
}
function btnStyle(kind) {
    if (kind === "primary") {
        return {
            padding: "6px 14px",
            borderRadius: 6,
            border: "none",
            background: "var(--accent)",
            color: "white",
            fontWeight: 600,
            cursor: "pointer",
            fontSize: 12,
        };
    }
    return {
        padding: "6px 14px",
        borderRadius: 6,
        border: "1px solid var(--border)",
        background: "transparent",
        color: "var(--text-primary)",
        cursor: "pointer",
        fontSize: 12,
    };
}
//# sourceMappingURL=IconCropper.js.map